import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  CalendarClock,
  Minus,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  X,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { GuidanceStrip } from "@/components/dashboard/GuidanceStrip";
import { DisciplineChip } from "@/components/shared/discipline-chip";
import { IntensityHoursHint } from "@/components/shared/IntensityHoursHint";
import { IntensityIndicator } from "@/components/shared/IntensityIndicator";
import { usePodShapes } from "@/hooks/use-pod-shapes";
import { useSolutionSignals } from "@/hooks/use-signals";
import { useSolutionDeletionAllowed } from "@/hooks/use-system-config";
import { useSession } from "@/lib/auth-client";
import { EXPECTATION_LEVELS, expectationLevelDef } from "@/lib/expectation-levels";
import { INTENSITY_MAX } from "@/lib/intensity";
import { jsonFetch } from "@/lib/json-fetch";
import { solutionDetailKeys } from "@/lib/query";
import { DeleteSolutionsDialog } from "./DeleteSolutionsDialog";
import { ExpectationBreakdown, type ExpectationBreakdownData } from "./ExpectationBreakdown";
import { FitRadar } from "./FitRadar";
import { ResourceDetailDrawer } from "./ResourceDetailDrawer";
import { SolutionConstraintsEditor } from "./SolutionConstraintsEditor";
import { SolutionDemandLinks } from "./SolutionDemandLinks";
import { SolutionPipelineChart } from "./SolutionPipelineChart";
import { SolutionSignalsSection } from "./SolutionSignalsSection";
import { SolutionWorkloadChart } from "./SolutionWorkloadChart";

type Role = "R1" | "R2" | "R3" | "R4";
type Composition = "Solo" | "Pair" | "Trio" | "Quad";

/** §3.5.5 + §3.6 Position data fetched from /api/dashboard/solutions/:id/pod-positions. */
interface PodPosition {
  /** null when synthesized from the template (no Pod yet materialized). */
  id: string | null;
  positionCode: Role;
  expectedDisciplineId: string;
  expectedDisciplineCode: string;
  expectedDisciplineOverrideReason: string | null;
  filledBy: {
    assignmentId: string;
    resourceId: string;
    resourceName: string;
    assignmentDisciplineId: string | null;
    assignmentDisciplineCode: string | null;
    mismatch: boolean;
  } | null;
}

interface SolutionDetail {
  id: string;
  name: string;
  status: string;
  startDate: string | null;
  intensity: number;
  podSize: Composition;
  podSizeOverrideReason: string | null;
  shapeId: string | null;
  shapeName: string | null;
  accountName: string;
  tier: string;
  tierId: string;
  tierOverridden: boolean;
  // Effective Expectation Level (per-Solution override, else Account default).
  expectationLevel: number;
  // The parent Account's Expectation Level, shown as the "Inherit" baseline.
  accountExpectationLevel: number;
  // True when this Solution carries its own override (solution.expectation_level
  // is non-null); drives the Inherited/Override badge + edit selector.
  expectationLevelOverridden: boolean;
  constraints: Array<{ id: string; name: string; group: string; inherited: boolean }>;
  assignments: Array<{
    id: string;
    role: string;
    points: number;
    resource: { id: string; name: string; email: string; initials: string; capacity: number };
  }>;
  fitSnapshot: {
    tier: string;
    overall: number;
    constraintMatch: number;
    availability: number;
    expectation: number;
    calculatedAt: string;
    // No-black-box Expectation breakdown (Story E3) — per-Primary Discipline
    // Rating, deviation, penalty step P, and points weight behind `expectation`
    // above. `null` for snapshots predating this slice.
    expectationBreakdown: {
      expectationLevel: number;
      penaltyStep: number;
      weightsUsed: { constraintMatch: number; availability: number; expectationLevel: number };
      members: Array<{
        resourceId: string;
        assignmentDisciplineId: string | null;
        rating: number;
        deviation: number;
        memberScore: number;
        points: number;
      }>;
    } | null;
  } | null;
  upcomingPodSchedule: Array<{
    id: string;
    composition: Composition;
    shapeName: string | null;
    effectiveStart: string;
    effectiveEnd: string | null;
  }>;
}

interface AtlasCandidate {
  resource: { id: string; name: string; email: string; initials: string };
  // `null` when the Solution has no Constraints — no Fit Score is computed, so
  // the picker hides the Fit badge and constraint-coverage line.
  score: number | null;
  coverage: { matched: number; total: number } | null;
  capacityNote: string | null;
  disciplines: Array<{ id: string; code: string; name: string }>;
}

interface DirectoryResource {
  id: string;
  name: string;
  email: string;
  initials: string;
  capacity: number;
  points: number;
  disciplines?: Array<{ id: string; code: string; name: string }>;
}

type Tab = "pod" | "fit" | "demand" | "hours" | "signals";

const ROLE_COLOR: Record<Role, string> = {
  R1: "var(--role-r1)",
  R2: "var(--role-r2)",
  R3: "var(--role-r3)",
  R4: "var(--role-r4)",
};

/** Solution status dot color (enum: Active | Pending | Closed). */
const STATUS_COLOR: Record<string, string> = {
  Active: "var(--fit-strong)",
  Pending: "var(--fit-moderate)",
  Closed: "var(--muted-foreground)",
};

/** Engagement Standard tier badge classes — mirrors the Solutions list TierTag. */
const TIER_CLASS: Record<string, string> = {
  Support: "bg-info-100 text-info-700",
  Growth: "bg-brand-50 text-brand-900",
  Standard: "bg-brand-100 text-brand-700",
  Strategic: "bg-brand-200 text-brand-900",
};

interface RoleAllocation {
  role: Role;
  points: number;
  filled: boolean;
}

/**
 * Intensity-vs-Allocation visual. Intensity (1–{@link INTENSITY_MAX}) is the
 * Pod's points-demand target (system-design §6.2.5 Availability = capacity
 * coverage — Σ Primary + Supporting points / Intensity). This bar stacks each
 * filled Primary's points — colored to match its Pod slot — plus a single
 * aggregate Supporting segment, against a target marker at the Intensity
 * value, so "meeting / exceeding / under" the Intensity is legible at a
 * glance and reacts live to the Intensity preview slider.
 */
function IntensityAllocationBar({
  intensity,
  allocations,
  supportingPoints = 0,
  onIntensityChange,
  intensitySaving,
}: {
  intensity: number;
  allocations: RoleAllocation[];
  /**
   * Aggregate Supporting-role points. The Fit Score engine's Availability is
   * now "capacity coverage" — Primary + Supporting points vs Intensity — so
   * this bar must include Supporting to stay in sync with the score.
   */
  supportingPoints?: number;
  /** When provided, the Intensity level is editable inline (−/+ 1–{@link INTENSITY_MAX}) and persists. */
  onIntensityChange?: (next: number) => void;
  intensitySaving?: boolean;
}) {
  const primaryTotal = allocations.reduce((sum, a) => sum + (a.filled ? a.points : 0), 0);
  const total = primaryTotal + supportingPoints;
  const delta = total - intensity;
  const status =
    delta < 0
      ? {
          label: `Understaffed · ${Math.abs(delta)} pt${Math.abs(delta) === 1 ? "" : "s"} short`,
          key: "under" as const,
        }
      : delta > 0
        ? { label: `Overstaffed · +${delta} pt${delta === 1 ? "" : "s"}`, key: "over" as const }
        : { label: "On Target", key: "met" as const };
  const statusColor =
    status.key === "met"
      ? "var(--fit-strong)"
      : status.key === "over"
        ? "var(--fit-moderate)"
        : "var(--fit-at-risk)";
  const statusBg =
    status.key === "met"
      ? "var(--fit-strong-bg)"
      : status.key === "over"
        ? "var(--fit-moderate-bg)"
        : "var(--fit-at-risk-bg)";

  // Scale so both the stacked total and the target marker always fit. When over,
  // the stack overshoots the marker; when under, a gap is left to the right.
  const scaleMax = Math.max(intensity, total, 1);
  const filled = allocations.filter((a) => a.filled && a.points > 0);

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Intensity
          </span>
          {onIntensityChange ? (
            <div
              className="flex items-center gap-0.5"
              title="Adjust Intensity (the Pod's points-demand target)"
            >
              <button
                type="button"
                onClick={() => onIntensityChange(intensity - 1)}
                disabled={intensity <= 1 || intensitySaving}
                className="rounded border border-border p-0.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Decrease Intensity"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="w-7 text-center font-display text-[13px] font-bold tabular-nums">
                L{intensity}
              </span>
              <button
                type="button"
                onClick={() => onIntensityChange(intensity + 1)}
                disabled={intensity >= INTENSITY_MAX || intensitySaving}
                className="rounded border border-border p-0.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
                aria-label="Increase Intensity"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          ) : (
            <span className="font-display text-[13px] font-bold tabular-nums">L{intensity}</span>
          )}
          <IntensityIndicator level={intensity} />
          <IntensityHoursHint intensity={intensity} />
        </div>
        <div className="flex items-center gap-2">
          <span className="font-display text-[14px] font-bold tabular-nums">
            {total}
            <span className="text-muted-foreground">/{intensity}</span>{" "}
            <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
              pts
            </span>
          </span>
          <span
            className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
            style={{ background: statusBg, color: statusColor }}
          >
            {status.label}
          </span>
        </div>
      </div>

      <div className="relative h-7 w-full overflow-hidden rounded bg-muted">
        <div className="flex h-full w-full">
          {filled.map((a) => (
            <div
              key={a.role}
              className="flex h-full items-center justify-center text-[10px] font-semibold text-white"
              style={{ width: `${(a.points / scaleMax) * 100}%`, background: ROLE_COLOR[a.role] }}
              title={`${a.role}: ${a.points} pt${a.points === 1 ? "" : "s"}`}
            >
              {(a.points / scaleMax) * 100 >= 9 ? a.role : ""}
            </div>
          ))}
          {supportingPoints > 0 && (
            <div
              className="flex h-full items-center justify-center text-[10px] font-semibold text-white"
              style={{
                width: `${(supportingPoints / scaleMax) * 100}%`,
                background: "var(--muted-foreground)",
              }}
              title={`Supporting: ${supportingPoints} pt${supportingPoints === 1 ? "" : "s"}`}
            >
              {(supportingPoints / scaleMax) * 100 >= 9 ? "Sup" : ""}
            </div>
          )}
        </div>
        {/* Target marker at the Intensity value. */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-foreground"
          style={{ left: `calc(${(intensity / scaleMax) * 100}% - 1px)` }}
          title={`Intensity target: ${intensity} pts`}
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
        {allocations.map((a) => (
          <span key={a.role} className="flex items-center gap-1 text-[10px]">
            <span
              className="h-2 w-2 rounded-sm"
              style={{
                background: a.filled ? ROLE_COLOR[a.role] : "transparent",
                border: a.filled ? "none" : "1px solid var(--border)",
              }}
              aria-hidden
            />
            <span className={a.filled ? "font-medium text-foreground" : "text-muted-foreground"}>
              {a.role} {a.filled ? `${a.points}` : "—"}
            </span>
          </span>
        ))}
        {supportingPoints > 0 && (
          <span className="flex items-center gap-1 text-[10px]">
            <span
              className="h-2 w-2 rounded-sm"
              style={{ background: "var(--muted-foreground)" }}
              aria-hidden
            />
            <span className="font-medium text-foreground">Supporting {supportingPoints}</span>
          </span>
        )}
        <span className="ml-auto flex items-center gap-1 text-[10px] text-muted-foreground">
          <span className="h-2.5 w-0.5 bg-foreground" aria-hidden /> target
        </span>
      </div>
    </div>
  );
}

/** One candidate row. Shows the Fit badge when scored; when the Solution has no
 * Constraints the score is `null`, so it falls back to an initials avatar and a
 * plain capacity line — a search-and-assign directory row with no Fit Score. */
function AtlasCandidateRow({
  c,
  onAssign,
}: {
  c: AtlasCandidate;
  onAssign: (resourceId: string) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onAssign(c.resource.id)}
      className="flex w-full items-center gap-3 rounded px-2 py-1.5 text-left hover:bg-muted"
    >
      {c.score === null ? (
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-foreground/85 text-[11px] font-semibold text-background">
          {c.resource.initials}
        </span>
      ) : (
        <span
          className={`flex min-w-[44px] flex-col items-center justify-center rounded-md px-2 py-1 ${
            c.score >= 75
              ? "bg-[var(--fit-strong-bg)] text-[var(--fit-strong)]"
              : c.score >= 50
                ? "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]"
                : "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]"
          }`}
        >
          <span className="font-display text-[22px] font-bold leading-none tabular-nums">
            {c.score}
          </span>
          <span className="text-[8px] font-semibold uppercase tracking-wide opacity-70">fit</span>
        </span>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-medium">{c.resource.name}</div>
        <div className="truncate text-[10px] text-muted-foreground">
          {c.coverage ? `${c.coverage.matched}/${c.coverage.total} constraints` : c.resource.email}
          {c.capacityNote ? ` · ${c.capacityNote}` : ""}
        </div>
        <GuidanceStrip resourceId={c.resource.id} />
      </div>
    </button>
  );
}

function AtlasPopover({
  solutionId,
  role,
  expectedDisciplineCode,
  onAssign,
  onClose,
}: {
  solutionId: string;
  role: Role;
  expectedDisciplineCode?: string;
  onAssign: (resourceId: string) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    searchRef.current?.focus();
  }, []);
  const { data, isLoading } = useQuery<{ candidates: AtlasCandidate[] }>({
    queryKey: ["atlas-suggest", solutionId, role],
    queryFn: () =>
      fetch(`/api/dashboard/solutions/${solutionId}/atlas-suggest?role=${role}&limit=all`).then(
        (r) => r.json(),
      ),
  });

  // Split the ranked candidates into those who hold the slot's Expected Discipline
  // and everyone else, so it's obvious when Atlas surfaces an out-of-Discipline pick.
  // Score order is preserved within each group.
  const { inDiscipline, others } = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.candidates ?? []).filter(
      (c) =>
        !q ||
        c.resource.name.toLowerCase().includes(q) ||
        c.resource.email.toLowerCase().includes(q),
    );
    if (!expectedDisciplineCode) return { inDiscipline: [], others: list };
    const holds = (c: AtlasCandidate) =>
      (c.disciplines ?? []).some((d) => d.code === expectedDisciplineCode);
    return { inDiscipline: list.filter(holds), others: list.filter((c) => !holds(c)) };
  }, [data, search, expectedDisciplineCode]);

  const total = inDiscipline.length + others.length;
  const grouped = Boolean(expectedDisciplineCode);
  // No Constraints ⇒ every candidate is unscored (a Solution-level property), so
  // there is no ranking. Swap the "Atlas suggests" header for a plain search-and-
  // assign label. Guard against non-`{candidates}` payloads (e.g. a 403 body).
  const allCandidates = data?.candidates ?? [];
  const noConstraints = allCandidates.length > 0 && allCandidates.every((c) => c.score === null);

  return (
    <div className="absolute left-0 right-0 top-full z-10 mt-1 rounded-md border border-border bg-card shadow-lg">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {noConstraints ? (
            <>Assign {role} · search Resources</>
          ) : (
            <>
              <Sparkles className="h-3 w-3" /> Atlas suggests · {role}
            </>
          )}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
      <div className="border-b border-border px-2 py-1.5">
        <input
          ref={searchRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search Resources…"
          className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-brand-500"
        />
      </div>
      <div className="max-h-64 overflow-y-auto p-1.5">
        {isLoading && <div className="px-2 py-3 text-[11px] text-muted-foreground">Ranking…</div>}
        {!isLoading && total === 0 && (
          <div className="px-2 py-3 text-[11px] text-muted-foreground">
            {search ? "No matches." : "No candidates."}
          </div>
        )}

        {!isLoading && grouped && total > 0 && (
          <>
            <div className="sticky top-0 z-[1] flex items-center gap-1.5 bg-card px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Expected Discipline</span>
              <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[9px] normal-case tracking-normal">
                {expectedDisciplineCode}
              </span>
              <span className="ml-auto tabular-nums">{inDiscipline.length}</span>
            </div>
            {inDiscipline.map((c) => (
              <AtlasCandidateRow key={c.resource.id} c={c} onAssign={onAssign} />
            ))}
            {inDiscipline.length === 0 && (
              <div className="px-2 py-2 text-[10px] italic text-muted-foreground">
                No candidate holds this Discipline.
              </div>
            )}

            {others.length > 0 && (
              <>
                <div className="sticky top-0 z-[1] mt-1 flex items-center gap-1.5 border-t border-[var(--fit-at-risk)]/30 bg-card px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--fit-at-risk)]">
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  <span>Other Disciplines · mismatch</span>
                  <span className="ml-auto tabular-nums">{others.length}</span>
                </div>
                {others.map((c) => (
                  <AtlasCandidateRow key={c.resource.id} c={c} onAssign={onAssign} />
                ))}
              </>
            )}
          </>
        )}

        {!isLoading &&
          !grouped &&
          others.map((c) => <AtlasCandidateRow key={c.resource.id} c={c} onAssign={onAssign} />)}
      </div>
    </div>
  );
}

function PodSlot({
  solutionId,
  role,
  pts,
  assignment,
  position,
  onAssign,
  onUnassign,
  onPointsChange,
  pointsSaving,
  atlasOpen,
  onAtlasToggle,
  onAtlasClose,
  onOpenResource,
}: {
  solutionId: string;
  role: Role;
  pts: number;
  assignment?: SolutionDetail["assignments"][number];
  position?: PodPosition;
  onAssign: (resourceId: string) => void;
  onUnassign: () => void;
  onPointsChange: (points: number) => void;
  pointsSaving?: boolean;
  atlasOpen: boolean;
  onAtlasToggle: () => void;
  onAtlasClose: () => void;
  onOpenResource: (resourceId: string) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const slotRef = useRef<HTMLDivElement>(null);

  // Dismiss the Atlas picker when the user clicks anywhere outside this slot
  // (the picker is a floating popover; without this it would linger on click-away).
  // Clicks inside the slot — the toggle button, the search box, a candidate row —
  // are ignored here so their own handlers run.
  useEffect(() => {
    if (!atlasOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (slotRef.current && !slotRef.current.contains(e.target as Node)) {
        onAtlasClose();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [atlasOpen, onAtlasClose]);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const resourceId = e.dataTransfer.getData("text/resource-id");
    if (resourceId) onAssign(resourceId);
  };

  return (
    <div
      ref={slotRef}
      className={`relative rounded-md border bg-card p-3 transition-colors ${
        dragOver ? "border-brand-500 bg-brand-50/40" : "border-border"
      }`}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("text/resource-id")) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <div className="mb-2 flex items-center justify-between gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-sm"
            style={{ background: ROLE_COLOR[role] }}
            aria-hidden
          />
          <span>{role}</span>
          {position && (
            <DisciplineChip
              code={position.expectedDisciplineCode}
              className="normal-case"
              title={
                position.expectedDisciplineOverrideReason
                  ? `Expected Discipline (override): ${position.expectedDisciplineOverrideReason}`
                  : "Expected Discipline (from template)"
              }
            />
          )}
        </div>
        {assignment ? (
          <div
            className="flex items-center gap-0.5"
            title="Adjust points allocated to this Resource"
          >
            <button
              type="button"
              onClick={() => onPointsChange(pts - 1)}
              disabled={pts <= 0 || pointsSaving}
              className="rounded border border-border p-0.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
              aria-label={`Decrease ${role} points`}
            >
              <Minus className="h-3 w-3" />
            </button>
            <span className="w-10 text-center text-[11px] font-semibold tabular-nums text-foreground">
              {pts} pts
            </span>
            <button
              type="button"
              onClick={() => onPointsChange(pts + 1)}
              disabled={pts >= assignment.resource.capacity || pointsSaving}
              className="rounded border border-border p-0.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
              aria-label={`Increase ${role} points`}
              title={`Up to the Resource's capacity (${assignment.resource.capacity})`}
            >
              <Plus className="h-3 w-3" />
            </button>
          </div>
        ) : (
          <span className="text-[10px] font-medium text-muted-foreground">{pts} pts target</span>
        )}
      </div>
      {assignment ? (
        <div className="space-y-1.5">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onOpenResource(assignment.resource.id)}
              className="-m-1 flex min-w-0 flex-1 items-center gap-2 rounded p-1 text-left hover:bg-muted"
              title={`View ${assignment.resource.name}`}
            >
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-[11px] font-semibold text-background">
                {assignment.resource.initials}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-[12px] font-medium">{assignment.resource.name}</div>
                <div className="truncate text-[10px] text-muted-foreground">
                  {assignment.resource.email}
                </div>
              </div>
            </button>
            <button
              type="button"
              onClick={onUnassign}
              className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-[var(--fit-at-risk)]"
              aria-label="Unassign"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
          {position?.filledBy?.mismatch && (
            <div
              className="flex items-center gap-1 rounded border border-[var(--fit-at-risk)]/40 bg-[var(--fit-at-risk-bg)] px-1.5 py-1 text-[10px] font-medium text-[var(--fit-at-risk)]"
              title={
                position.filledBy.assignmentDisciplineCode
                  ? `Resource filling under ${position.filledBy.assignmentDisciplineCode}; Expected ${position.expectedDisciplineCode}`
                  : `Resource holds no Discipline; Expected ${position.expectedDisciplineCode}`
              }
            >
              {position.filledBy.assignmentDisciplineCode
                ? `Discipline Mismatch · filling as ${position.filledBy.assignmentDisciplineCode}`
                : "Discipline Mismatch · no Discipline held"}
            </div>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={onAtlasToggle}
            className="flex w-full items-center justify-center gap-1 rounded border border-dashed border-border bg-muted/30 px-2 py-2 text-[11px] font-medium text-foreground/80 hover:border-foreground/30 hover:bg-muted"
          >
            <Sparkles className="h-3 w-3" />
            Atlas suggest · or drop a Resource
          </button>
          {atlasOpen && (
            <AtlasPopover
              solutionId={solutionId}
              role={role}
              expectedDisciplineCode={position?.expectedDisciplineCode}
              onAssign={(rid) => {
                onAtlasClose();
                onAssign(rid);
              }}
              onClose={onAtlasClose}
            />
          )}
        </>
      )}
    </div>
  );
}

function EditSolutionButton({
  solution,
  onSaved,
}: {
  solution: SolutionDetail;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(solution.name);
  const [level, setLevel] = useState(solution.intensity);
  const [tierId, setTierId] = useState(solution.tierId);
  const [status, setStatus] = useState(solution.status);
  // "" = inherit from Account (override cleared); "1".."5" = per-Solution override.
  const [expLevel, setExpLevel] = useState(
    solution.expectationLevelOverridden ? String(solution.expectationLevel) : "",
  );

  const { data: ref } = useQuery<{ tiers: Array<{ id: string; name: string }> }>({
    queryKey: ["dashboard-reference"],
    queryFn: () =>
      jsonFetch<{ tiers: Array<{ id: string; name: string }> }>("/api/dashboard/reference"),
    enabled: open,
  });

  const save = useMutation({
    mutationFn: async () => {
      const patch: {
        name?: string;
        intensity?: number;
        tierId?: string | null;
        expectationLevel?: number | null;
        status?: string;
      } = {};
      if (name.trim() !== solution.name) patch.name = name.trim();
      if (level !== solution.intensity) patch.intensity = level;
      if (tierId !== solution.tierId) patch.tierId = tierId;
      // Compare against the current override state: null when inheriting.
      const currentExp = solution.expectationLevelOverridden ? solution.expectationLevel : null;
      const nextExp = expLevel === "" ? null : Number(expLevel);
      if (nextExp !== currentExp) patch.expectationLevel = nextExp;
      if (status !== solution.status) patch.status = status;
      if (Object.keys(patch).length === 0) return { ok: true };
      const r = await fetch(`/api/dashboard/solutions/${solution.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      if (!r.ok) {
        const body = await r.json();
        throw new Error(body.error ?? "Failed");
      }
      return r.json();
    },
    onSuccess: () => {
      setOpen(false);
      onSaved();
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setName(solution.name);
          setLevel(solution.intensity);
          setTierId(solution.tierId);
          setExpLevel(solution.expectationLevelOverridden ? String(solution.expectationLevel) : "");
          setStatus(solution.status);
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 rounded border border-border bg-card px-2 py-1 text-[11px] font-medium text-foreground hover:bg-muted"
        title="Edit Solution"
      >
        <Pencil className="h-3 w-3" />
        Edit
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-12">
          <div className="w-full max-w-[460px] rounded-md border border-border bg-background p-4 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-[15px] font-bold">Edit Solution</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <label className="mb-3 block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Name
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
              />
            </label>

            <div className="mb-3">
              <div className="mb-1 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Intensity
                </span>
                <span className="font-display text-[16px] font-bold">{level}</span>
              </div>
              <input
                type="range"
                min={1}
                max={INTENSITY_MAX}
                value={level}
                onChange={(e) => setLevel(Number(e.target.value))}
                className="w-full accent-[var(--intensity-8)]"
              />
              <div className="mt-2 flex items-center justify-between">
                <IntensityIndicator level={level} />
                <IntensityHoursHint intensity={level} />
              </div>
            </div>

            <label className="mb-3 block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Tier
                {solution.tierOverridden && (
                  <span className="ml-2 rounded bg-[var(--intensity-8)]/10 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-[var(--intensity-8)]">
                    Override
                  </span>
                )}
              </span>
              <select
                value={tierId}
                onChange={(e) => setTierId(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
              >
                {(ref?.tiers ?? [{ id: solution.tierId, name: solution.tier }]).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Overrides the Account's default tier for this Solution only.
              </p>
            </label>

            <label className="mb-3 block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Expectation Level
                {expLevel !== "" && (
                  <span className="ml-2 rounded bg-[var(--intensity-8)]/10 px-1.5 py-0.5 text-[9px] font-semibold normal-case tracking-normal text-[var(--intensity-8)]">
                    Override
                  </span>
                )}
              </span>
              <select
                value={expLevel}
                onChange={(e) => setExpLevel(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
              >
                <option value="">
                  Inherit from Account (level {solution.accountExpectationLevel} ·{" "}
                  {expectationLevelDef(solution.accountExpectationLevel)?.label ??
                    solution.accountExpectationLevel}
                  )
                </option>
                {EXPECTATION_LEVELS.map((l) => (
                  <option key={l.level} value={String(l.level)}>
                    {l.level} · {l.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Overrides the Account's default Expectation Level for this Solution only.
              </p>
            </label>

            <label className="mb-3 block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Status
              </span>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
              >
                {["Active", "Pending", "Closed"].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>

            {save.isError && (
              <div className="mb-3 rounded bg-[var(--fit-at-risk-bg)] px-2 py-1.5 text-[11px] text-[var(--fit-at-risk)]">
                {String((save.error as Error).message).slice(0, 200)}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded border border-border px-3 py-1 text-[12px] hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={save.isPending || name.trim().length === 0}
                onClick={() => save.mutate()}
                className="rounded bg-foreground px-3 py-1 text-[12px] font-semibold text-background disabled:opacity-40"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function DeleteSolutionButton({
  solutionId,
  onDeleted,
}: {
  solutionId: string;
  onDeleted: () => void;
}) {
  const { data: session } = useSession();
  const deletionAllowed = useSolutionDeletionAllowed();
  const [open, setOpen] = useState(false);
  if (session?.user?.role !== "RM" || !deletionAllowed) return null;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[12px] text-[var(--fit-at-risk)] transition hover:bg-[var(--fit-at-risk-bg)]"
        title="Delete Solution"
      >
        <Trash2 className="h-3.5 w-3.5" /> Delete
      </button>
      <DeleteSolutionsDialog
        open={open}
        onOpenChange={setOpen}
        ids={[solutionId]}
        onDeleted={onDeleted}
      />
    </>
  );
}

function ShapePickerButton({
  solution,
  onSaved,
}: {
  solution: SolutionDetail;
  onSaved: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [shapeId, setShapeId] = useState<string>(solution.shapeId ?? "");
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState<"now" | "future">("now");
  const [effectiveStart, setEffectiveStart] = useState("");
  const { data: shapes = [] } = usePodShapes();

  const save = useMutation({
    mutationFn: async () => {
      if (!shapeId) throw new Error("Select a Shape");
      if (mode === "future") {
        if (!effectiveStart) throw new Error("Effective date required");
        const r = await fetch(`/api/dashboard/solutions/${solution.id}/pod-schedule`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            shapeId,
            effectiveStart: new Date(effectiveStart).toISOString(),
            reason,
          }),
        });
        if (!r.ok) {
          const body = await r.json();
          throw new Error(body.error ?? "Failed");
        }
        return r.json();
      }
      const r = await fetch(`/api/dashboard/solutions/${solution.id}/shape`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shapeId, reason }),
      });
      if (!r.ok) {
        const body = await r.json();
        throw new Error(body.error ?? "Failed");
      }
      return r.json();
    },
    onSuccess: () => {
      setOpen(false);
      setReason("");
      setEffectiveStart("");
      setMode("now");
      onSaved();
    },
  });

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setShapeId(solution.shapeId ?? "");
          setOpen(true);
        }}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium normal-case tracking-normal text-foreground hover:bg-muted"
        title="Change Pod Shape / size"
      >
        {solution.shapeName ?? solution.podSize}
        {solution.shapeName && <span className="text-muted-foreground">· {solution.podSize}</span>}
        <Pencil className="h-2.5 w-2.5" />
      </button>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-12">
          <div className="w-full max-w-[440px] rounded-md border border-border bg-background p-4 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="font-display text-[15px] font-bold">Change Pod Shape</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-3 text-[12px] text-muted-foreground">
              {mode === "now"
                ? "Apply immediately — ends the current Pod and any roles that no longer fit."
                : "Schedule for a future date — current Pod stays active until then."}
            </p>
            <div className="mb-3 flex gap-1 rounded-md border border-border bg-card p-0.5 text-[11px]">
              <button
                type="button"
                onClick={() => setMode("now")}
                className={`flex-1 rounded px-2 py-1 ${mode === "now" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                Apply now
              </button>
              <button
                type="button"
                onClick={() => setMode("future")}
                className={`flex-1 rounded px-2 py-1 ${mode === "future" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"}`}
              >
                Schedule
              </button>
            </div>
            <div className="mb-3 max-h-[260px] space-y-1.5 overflow-y-auto">
              {shapes.length === 0 && (
                <p className="rounded-md border border-dashed border-border bg-card/50 px-3 py-2 text-[11px] text-muted-foreground">
                  No Shapes defined. Build Shapes under Configuration ▸ Shapes first.
                </p>
              )}
              {shapes.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setShapeId(s.id)}
                  className={`flex w-full items-center gap-2 rounded border px-2.5 py-1.5 text-left text-[12px] ${
                    shapeId === s.id
                      ? "border-foreground bg-foreground/5"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  <span
                    className={`h-2.5 w-2.5 shrink-0 rounded-full border ${
                      shapeId === s.id ? "border-foreground bg-foreground" : "border-border"
                    }`}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium">{s.name}</span>{" "}
                    <span className="text-muted-foreground">· {s.composition}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    {[
                      s.disciplineR1Code,
                      s.disciplineR2Code,
                      s.disciplineR3Code,
                      s.disciplineR4Code,
                    ]
                      .filter((code): code is string => Boolean(code))
                      .map((code, i) => (
                        <DisciplineChip key={`${s.id}-${code}-${String(i)}`} code={code} />
                      ))}
                  </span>
                </button>
              ))}
            </div>
            {mode === "future" && (
              <label className="mb-3 block">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Effective from
                </span>
                <input
                  type="date"
                  value={effectiveStart}
                  onChange={(e) => setEffectiveStart(e.target.value)}
                  className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
                />
              </label>
            )}
            <label className="mb-3 block">
              <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Reason (required, audit-logged)
              </span>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. Account upgraded to Trio for Q3 strategic engagement"
                className="mt-1 w-full rounded-md border border-border bg-card p-2 text-[12px] outline-none focus:border-foreground/30"
                rows={3}
              />
            </label>
            {save.isError && (
              <div className="mb-3 rounded bg-[var(--fit-at-risk-bg)] px-2 py-1.5 text-[11px] text-[var(--fit-at-risk)]">
                {String((save.error as Error).message).slice(0, 200)}
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded border border-border px-3 py-1 text-[12px] hover:bg-muted"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={save.isPending || reason.trim().length < 3}
                onClick={() => save.mutate()}
                className="rounded bg-foreground px-3 py-1 text-[12px] font-semibold text-background disabled:opacity-40"
              >
                {save.isPending ? "Saving…" : "Save override"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/** One draggable Resource row in the browser. */
function ResourceRow({ r }: { r: DirectoryResource }) {
  const over = r.points > r.capacity;
  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("text/resource-id", r.id);
        e.dataTransfer.effectAllowed = "copy";
      }}
      className="flex cursor-grab items-center gap-2 rounded px-2 py-1.5 active:cursor-grabbing hover:bg-muted"
    >
      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-[10px] font-semibold text-background">
        {r.initials}
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-[12px] font-medium">{r.name}</div>
        <div className="truncate text-[10px] text-muted-foreground">{r.email}</div>
      </div>
      <span
        className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
          over
            ? "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]"
            : r.points >= r.capacity
              ? "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]"
              : "bg-muted text-muted-foreground"
        }`}
      >
        {r.points}/{r.capacity}
      </span>
    </div>
  );
}

function ResourceBrowser({ expectedDisciplineCodes }: { expectedDisciplineCodes: string[] }) {
  const { data } = useQuery<{ resources: DirectoryResource[] }>({
    queryKey: ["dashboard-resources"],
    queryFn: () => jsonFetch<{ resources: DirectoryResource[] }>("/api/dashboard/resources"),
  });
  const [query, setQuery] = useState("");

  const expectedSet = useMemo(() => new Set(expectedDisciplineCodes), [expectedDisciplineCodes]);
  const grouped = expectedDisciplineCodes.length > 0;

  // Split into two groupings so it's obvious when a Resource is outside the Pod's
  // Expected Discipline: those holding it come first, everyone else below.
  const { inDiscipline, others } = useMemo(() => {
    const list = data?.resources ?? [];
    const q = query.trim().toLowerCase();
    const matched = q ? list.filter((r) => r.name.toLowerCase().includes(q)) : list;
    if (!grouped) return { inDiscipline: [], others: matched };
    const inDiscipline = matched.filter((r) =>
      (r.disciplines ?? []).some((d) => expectedSet.has(d.code)),
    );
    const others = matched.filter(
      (r) => !(r.disciplines ?? []).some((d) => expectedSet.has(d.code)),
    );
    return { inDiscipline, others };
  }, [data, query, grouped, expectedSet]);

  const total = inDiscipline.length + others.length;

  return (
    <div className="rounded-md border border-border bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Resources · drag onto a slot
        </span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className="h-6 w-40 rounded border border-border bg-background px-2 text-[11px] outline-none focus:border-foreground/30"
        />
      </div>
      <div className="max-h-64 overflow-y-auto p-1.5">
        {total === 0 && (
          <div className="px-3 py-6 text-center text-[11px] text-muted-foreground">No matches.</div>
        )}

        {grouped ? (
          <>
            {total > 0 && (
              <div className="sticky top-0 z-[1] flex items-center gap-1.5 bg-card px-2 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span>Expected Discipline</span>
                <span className="rounded-full bg-muted px-1.5 py-0.5 font-mono text-[9px] normal-case tracking-normal">
                  {expectedDisciplineCodes.join("·")}
                </span>
                <span className="ml-auto tabular-nums">{inDiscipline.length}</span>
              </div>
            )}
            {inDiscipline.map((r) => (
              <ResourceRow key={r.id} r={r} />
            ))}
            {total > 0 && inDiscipline.length === 0 && (
              <div className="px-2 py-2 text-[10px] italic text-muted-foreground">
                No available Resource holds this Discipline.
              </div>
            )}

            {others.length > 0 && (
              <>
                <div className="sticky top-0 z-[1] mt-1 flex items-center gap-1.5 border-t border-[var(--fit-at-risk)]/30 bg-card px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-[var(--fit-at-risk)]">
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  <span>Other Disciplines · mismatch</span>
                  <span className="ml-auto tabular-nums">{others.length}</span>
                </div>
                {others.map((r) => (
                  <ResourceRow key={r.id} r={r} />
                ))}
              </>
            )}
          </>
        ) : (
          others.map((r) => <ResourceRow key={r.id} r={r} />)
        )}
      </div>
    </div>
  );
}

/**
 * Supporting Roles — floaters, cloud architects, and other Disciplines attached
 * to a Solution outside the Pod. Per non-negotiable #2 they never affect the Fit
 * Score, but their points consume Resource capacity, so each row surfaces the
 * Resource's total capacity load to keep availability decisions honest.
 */
function SupportingSection({
  supporting,
  onAdd,
  onRemove,
  onPointsChange,
  adding,
  pending,
  onOpenResource,
}: {
  supporting: SolutionDetail["assignments"];
  onAdd: (resourceId: string) => void;
  onRemove: (assignmentId: string) => void;
  onPointsChange: (assignmentId: string, points: number) => void;
  adding?: boolean;
  pending?: boolean;
  onOpenResource: (resourceId: string) => void;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { data } = useQuery<{ resources: DirectoryResource[] }>({
    queryKey: ["dashboard-resources"],
    queryFn: () => jsonFetch<{ resources: DirectoryResource[] }>("/api/dashboard/resources"),
  });
  const resourceById = useMemo(
    () => new Map((data?.resources ?? []).map((r) => [r.id, r])),
    [data],
  );
  const assignedIds = useMemo(() => new Set(supporting.map((s) => s.resource.id)), [supporting]);
  const candidates = useMemo(() => {
    const list = (data?.resources ?? []).filter((r) => !assignedIds.has(r.id));
    const q = query.trim().toLowerCase();
    return q
      ? list.filter((r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q))
      : list;
  }, [data, query, assignedIds]);

  const totalPoints = supporting.reduce((s, a) => s + a.points, 0);

  const loadBadge = (load: number, cap: number) =>
    cap > 0 && load > cap
      ? "bg-[var(--fit-at-risk-bg)] text-[var(--fit-at-risk)]"
      : cap > 0 && load >= cap
        ? "bg-[var(--fit-moderate-bg)] text-[var(--fit-moderate)]"
        : "bg-muted text-muted-foreground";

  return (
    <section>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Supporting Roles
          {supporting.length > 0 && (
            <span className="ml-2 font-normal normal-case tracking-normal text-muted-foreground">
              {totalPoints} pt{totalPoints === 1 ? "" : "s"} · capacity only
            </span>
          )}
        </span>
        <button
          type="button"
          onClick={() => setPickerOpen((v) => !v)}
          className="text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          {pickerOpen ? "Close" : "+ Add Supporting"}
        </button>
      </div>
      <p className="mb-2 text-[11px] text-muted-foreground">
        Floaters, cloud architects, and other Disciplines. Points count against Resource capacity
        but never affect the Fit Score.
      </p>

      {pickerOpen && (
        <div className="mb-2 rounded-md border border-border bg-card">
          <div className="border-b border-border px-2 py-1.5">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search Resources to add…"
              className="w-full rounded border border-border bg-background px-2 py-1 text-[12px] outline-none focus:border-brand-500"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1.5">
            {candidates.length === 0 && (
              <div className="px-2 py-3 text-[11px] text-muted-foreground">
                {query ? "No matches." : "No Resources available."}
              </div>
            )}
            {candidates.map((r) => (
              <button
                key={r.id}
                type="button"
                disabled={adding}
                onClick={() => {
                  onAdd(r.id);
                  setPickerOpen(false);
                  setQuery("");
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted disabled:opacity-50"
              >
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-[10px] font-semibold text-background">
                  {r.initials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[12px] font-medium">{r.name}</div>
                  <div className="flex items-center gap-1.5 truncate text-[10px] text-muted-foreground">
                    {(r.disciplines ?? []).map((d) => (
                      <DisciplineChip
                        key={d.id}
                        code={d.code}
                        title={d.name}
                        className="shrink-0"
                      />
                    ))}
                    <span className="truncate">{r.email}</span>
                  </div>
                </div>
                <span
                  className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${loadBadge(r.points, r.capacity)}`}
                  title="Current capacity load across all Solutions"
                >
                  {r.points}/{r.capacity}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {supporting.length === 0 ? (
        <div className="rounded-md border border-dashed border-border bg-card/50 px-3 py-4 text-center text-[11px] text-muted-foreground">
          No Supporting Resources assigned.
        </div>
      ) : (
        <div className="space-y-1.5">
          {supporting.map((a) => {
            const r = resourceById.get(a.resource.id);
            const load = r?.points ?? 0;
            const cap = r?.capacity ?? 0;
            return (
              <div
                key={a.id}
                className="flex items-center gap-2 rounded-md border border-border bg-card p-2"
              >
                <button
                  type="button"
                  onClick={() => onOpenResource(a.resource.id)}
                  className="-m-1 flex min-w-0 flex-1 items-center gap-2 rounded p-1 text-left hover:bg-muted"
                  title={`View ${a.resource.name}`}
                >
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-[11px] font-semibold text-background">
                    {a.resource.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[12px] font-medium">{a.resource.name}</div>
                    <div className="flex items-center gap-1.5 truncate text-[10px] text-muted-foreground">
                      {(r?.disciplines ?? []).map((d) => (
                        <DisciplineChip
                          key={d.id}
                          code={d.code}
                          title={d.name}
                          className="shrink-0"
                        />
                      ))}
                      <span className="truncate">{a.resource.email}</span>
                    </div>
                  </div>
                </button>
                {cap > 0 && (
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${loadBadge(load, cap)}`}
                    title="Resource capacity load across all Solutions"
                  >
                    {load}/{cap}
                  </span>
                )}
                <div
                  className="flex items-center gap-0.5"
                  title="Adjust Supporting points (counts against Resource capacity)"
                >
                  <button
                    type="button"
                    onClick={() => onPointsChange(a.id, a.points - 1)}
                    disabled={a.points <= 0 || pending}
                    className="rounded border border-border p-0.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Decrease Supporting points"
                    title="0 pts attaches the Resource without consuming capacity; use the trash icon to remove"
                  >
                    <Minus className="h-3 w-3" />
                  </button>
                  <span className="w-10 text-center text-[11px] font-semibold tabular-nums">
                    {a.points} pts
                  </span>
                  <button
                    type="button"
                    onClick={() => onPointsChange(a.id, a.points + 1)}
                    disabled={a.points >= a.resource.capacity || pending}
                    className="rounded border border-border p-0.5 text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-30"
                    aria-label="Increase Supporting points"
                    title={`Up to the Resource's capacity (${a.resource.capacity})`}
                  >
                    <Plus className="h-3 w-3" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(a.id)}
                  className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-[var(--fit-at-risk)]"
                  aria-label="Remove Supporting"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function SolutionDetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("pod");
  const [browseOpen, setBrowseOpen] = useState(false);
  // Only one Atlas-suggest box open at a time (per pod position). Lifting this
  // out of PodSlot lets opening one — or the Browse panel — close the others.
  const [openAtlasRole, setOpenAtlasRole] = useState<Role | null>(null);
  const [openResourceId, setOpenResourceId] = useState<string | null>(null);
  const qc = useQueryClient();
  const { data } = useQuery<SolutionDetail>({
    queryKey: solutionDetailKeys.detail(id),
    queryFn: () => jsonFetch<SolutionDetail>(`/api/dashboard/solutions/${id}`),
  });
  // Open-signal count for the Signals tab badge (shares cache with the tab's own fetch).
  const { data: solutionSignals } = useSolutionSignals(id);
  const openSignalCount = solutionSignals?.signals.length ?? 0;

  const [previewIntensity, setPreviewIntensity] = useState<number | null>(null);
  useEffect(() => {
    if (data) setPreviewIntensity(data.intensity);
  }, [data]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // The Resource drawer stacks on top and owns its own ESC; don't also
      // close the Solution drawer underneath it.
      if (openResourceId) return;
      // An open Atlas picker is dismissed first, before the Solution drawer.
      if (openAtlasRole) {
        setOpenAtlasRole(null);
        return;
      }
      onClose();
    };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose, openResourceId, openAtlasRole]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: solutionDetailKeys.detail(id) });
    qc.invalidateQueries({ queryKey: ["dashboard-solutions"] });
    qc.invalidateQueries({ queryKey: ["dashboard-resources"] });
    qc.invalidateQueries({ queryKey: ["dashboard-heatmap"] });
    qc.invalidateQueries({ queryKey: ["atlas-suggest", id] });
    // §3.5.5 + §6.2.1: tier or pod_size changes re-derive the Expected Discipline
    // per Position from the template, so the positions list must refresh too.
    qc.invalidateQueries({ queryKey: ["solution-pod-positions", id] });
    // Discipline reports aggregate across all Solutions; tier changes can flip
    // counts (e.g. moving a Solution between tiers changes Pod Composition Drift
    // and Discipline Mismatch totals).
    qc.invalidateQueries({ queryKey: ["report"] });
  };

  const assignMutation = useMutation({
    mutationFn: async ({ resourceId, role }: { resourceId: string; role: Role }) => {
      // §3.5.5: any Resource can fill any Pod Position — a Discipline gap never
      // blocks the assignment. The server flags a mismatch (audit-logged) and the
      // slot shows the Discipline Mismatch indicator.
      const r = await fetch(`/api/dashboard/solutions/${id}/assignments`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resourceId, role }),
      });
      if (!r.ok) {
        const errBody = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(errBody.error ?? "Assign failed");
      }
      return (await r.json()) as {
        disciplineMismatch?: {
          expectedDisciplineCode: string | null;
          assignmentDisciplineCode: string | null;
        } | null;
      } | null;
    },
    onSuccess: (data) => {
      invalidate();
      if (data?.disciplineMismatch) {
        const { assignmentDisciplineCode, expectedDisciplineCode } = data.disciplineMismatch;
        toast.warning(
          assignmentDisciplineCode
            ? `Discipline Mismatch — filling as ${assignmentDisciplineCode}, Expected ${expectedDisciplineCode ?? "—"}`
            : `Discipline Mismatch — Resource holds no Discipline, Expected ${expectedDisciplineCode ?? "—"}`,
        );
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Assign failed");
    },
  });

  const unassignMutation = useMutation({
    mutationFn: async (role: Role) => {
      const r = await fetch(`/api/dashboard/solutions/${id}/assignments/${role}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error("Unassign failed");
      return r.json();
    },
    onSuccess: invalidate,
  });

  const updatePointsMutation = useMutation({
    mutationFn: async ({ role, points }: { role: Role; points: number }) => {
      const r = await fetch(`/api/dashboard/solutions/${id}/assignments/${role}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ points }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to update points");
      }
      return r.json();
    },
    onSuccess: invalidate,
  });

  const assignSupportingMutation = useMutation({
    mutationFn: async (resourceId: string) => {
      const r = await fetch(`/api/dashboard/solutions/${id}/supporting`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resourceId }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to add Supporting");
      }
      return r.json();
    },
    onSuccess: invalidate,
  });

  const unassignSupportingMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      const r = await fetch(`/api/dashboard/solutions/${id}/supporting/${assignmentId}`, {
        method: "DELETE",
      });
      if (!r.ok) throw new Error("Failed to remove Supporting");
      return r.json();
    },
    onSuccess: invalidate,
  });

  const supportingPointsMutation = useMutation({
    mutationFn: async ({ assignmentId, points }: { assignmentId: string; points: number }) => {
      const r = await fetch(`/api/dashboard/solutions/${id}/supporting/${assignmentId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ points }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to update points");
      }
      return r.json();
    },
    onSuccess: invalidate,
  });

  const intensityMutation = useMutation({
    mutationFn: async (intensity: number) => {
      const r = await fetch(`/api/dashboard/solutions/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ intensity }),
      });
      if (!r.ok) {
        const body = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? "Failed to update Intensity");
      }
      return r.json();
    },
    onSuccess: invalidate,
  });

  const roles: Role[] = !data
    ? ["R1"]
    : data.podSize === "Solo"
      ? ["R1"]
      : data.podSize === "Pair"
        ? ["R1", "R2"]
        : data.podSize === "Trio"
          ? ["R1", "R2", "R3"]
          : ["R1", "R2", "R3", "R4"];
  const byRole = new Map(data?.assignments.map((a) => [a.role, a]) ?? []);
  const pointsFor = (r: Role) => (r === "R1" ? 2 : 1);
  const allocations: RoleAllocation[] = roles.map((r) => {
    const a = byRole.get(r);
    return { role: r, points: a?.points ?? pointsFor(r), filled: Boolean(a) };
  });
  // All active Supporting Resources. Supporting is first-class staffing and may sit
  // at 0 points (attached to the Solution without consuming capacity), so 0-point
  // rows are shown and editable rather than hidden.
  const supporting = (data?.assignments ?? []).filter((a) => a.role === "Supporting");
  // Fit Score's Availability is now "capacity coverage" — Primary + Supporting
  // points vs Intensity — so the allocation bar must include this total too.
  const supportingPoints = supporting.reduce((sum, a) => sum + a.points, 0);

  // §3.5.5 + §3.6 Position-level Expected Discipline + mismatch state.
  const { data: positions } = useQuery<PodPosition[]>({
    queryKey: ["solution-pod-positions", id],
    queryFn: () => jsonFetch<PodPosition[]>(`/api/dashboard/solutions/${id}/pod-positions`),
    enabled: !!data,
  });
  const positionByRole = new Map((positions ?? []).map((p) => [p.positionCode, p]));

  // §3.5.5 + Story E3: resolve each Expectation breakdown member (resourceId only)
  // against the resource name/role (from assignments) and the Assignment Discipline
  // code (from the Pod Position it fills) so the breakdown never shows a bare ID.
  const assignmentDisciplineCodeByResourceId = new Map(
    (positions ?? [])
      .filter(
        (p): p is PodPosition & { filledBy: NonNullable<PodPosition["filledBy"]> } =>
          p.filledBy !== null,
      )
      .map((p) => [p.filledBy.resourceId, p.filledBy.assignmentDisciplineCode]),
  );
  const expectationBreakdownData: ExpectationBreakdownData | null = data?.fitSnapshot
    ?.expectationBreakdown
    ? {
        expectationLevel: data.fitSnapshot.expectationBreakdown.expectationLevel,
        penaltyStep: data.fitSnapshot.expectationBreakdown.penaltyStep,
        overall: data.fitSnapshot.expectation,
        members: data.fitSnapshot.expectationBreakdown.members.map((m) => {
          const a = (data?.assignments ?? []).find((row) => row.resource.id === m.resourceId);
          return {
            resourceId: m.resourceId,
            resourceName: a?.resource.name ?? "Unknown Resource",
            role: a?.role ?? "—",
            assignmentDisciplineCode:
              assignmentDisciplineCodeByResourceId.get(m.resourceId) ?? null,
            rating: m.rating,
            deviation: m.deviation,
            memberScore: m.memberScore,
            points: m.points,
          };
        }),
      }
    : null;

  const cancelSchedule = useMutation({
    mutationFn: async (entryId: string) => {
      const r = await fetch(`/api/dashboard/pod-schedule/${entryId}`, { method: "DELETE" });
      if (!r.ok) {
        const body = await r.json();
        throw new Error(body.error ?? "Failed");
      }
      return r.json();
    },
    onSuccess: invalidate,
  });

  const hasR1 = data?.assignments.some((a) => a.role === "R1") ?? false;
  // The Fit Score needs ≥1 Constraint to score Constraint Match against. Without
  // Constraints it is suppressed everywhere; Resources can still be assigned.
  const hasConstraints = (data?.constraints.length ?? 0) > 0;

  return (
    <>
      <button
        type="button"
        aria-label="Close drawer"
        className="fixed inset-0 z-40 bg-black/40"
        onClick={onClose}
      />
      <aside className="fixed right-0 top-0 z-50 flex h-screen w-full max-w-[840px] flex-col border-l border-border bg-background shadow-[0_0_40px_rgba(0,24,71,0.18)]">
        <header className="flex items-start justify-between gap-4 border-b border-border px-5 py-4">
          <div className="min-w-0">
            {!data ? (
              <div className="text-[12px] text-muted-foreground">Loading…</div>
            ) : (
              <>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {data.accountName}
                </div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-[18px] font-bold leading-tight">{data.name}</h2>
                  <EditSolutionButton solution={data} onSaved={invalidate} />
                  <DeleteSolutionButton solutionId={data.id} onDeleted={onClose} />
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px]">
                  <span
                    className="inline-flex items-center gap-1.5 text-muted-foreground"
                    title={`Status: ${data.status}`}
                  >
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{ background: STATUS_COLOR[data.status] ?? "var(--muted-foreground)" }}
                      aria-hidden
                    />
                    <span className="font-medium text-foreground">{data.status}</span>
                  </span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                      TIER_CLASS[data.tier] ?? "bg-muted text-foreground"
                    }`}
                    title={
                      data.tierOverridden
                        ? "Engagement Standard tier (overridden for this Solution)"
                        : "Engagement Standard tier"
                    }
                  >
                    {data.tier}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground"
                    title={
                      data.expectationLevelOverridden
                        ? `Expectation Level ${data.expectationLevel} (overridden for this Solution; Account default is ${data.accountExpectationLevel})`
                        : `Expectation Level ${data.expectationLevel} (inherited from Account)`
                    }
                  >
                    <span className="text-muted-foreground">Exp</span>
                    {data.expectationLevel} ·{" "}
                    {expectationLevelDef(data.expectationLevel)?.label ?? data.expectationLevel}
                    <span
                      className={`ml-0.5 rounded px-1 py-px text-[8px] font-semibold uppercase tracking-wide ${
                        data.expectationLevelOverridden
                          ? "bg-[var(--intensity-8)]/10 text-[var(--intensity-8)]"
                          : "bg-transparent text-muted-foreground"
                      }`}
                    >
                      {data.expectationLevelOverridden ? "Override" : "Inherited"}
                    </span>
                  </span>
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <nav className="flex border-b border-border bg-muted/30 text-[12px]">
          {(
            [
              ["pod", "Pod & Fit"],
              ["fit", "Fit Calculator"],
              ["demand", "Demands"],
              ["hours", "Forecast"],
              ["signals", "Signals"],
            ] as [Tab, string][]
          ).map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`flex items-center gap-1.5 px-4 py-2 font-medium transition ${
                tab === k
                  ? "border-b-2 border-foreground text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {l}
              {k === "signals" && openSignalCount > 0 && (
                <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-warning-100 px-1 text-[10px] font-semibold text-warning-700 dark:text-warning-400">
                  {openSignalCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!data ? null : tab === "pod" ? (
            <div className="space-y-5">
              <IntensityAllocationBar
                intensity={data.intensity}
                allocations={allocations}
                supportingPoints={supportingPoints}
                onIntensityChange={(next) => intensityMutation.mutate(next)}
                intensitySaving={intensityMutation.isPending}
              />

              <section>
                <div className="mb-2 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      Pod Composition
                    </span>
                    <ShapePickerButton solution={data} onSaved={invalidate} />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setBrowseOpen((v) => !v);
                      setOpenAtlasRole(null);
                    }}
                    className="shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground"
                  >
                    {browseOpen ? "Hide resources" : "Browse resources"}
                  </button>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {roles.map((r) => (
                    <PodSlot
                      key={r}
                      solutionId={id}
                      role={r}
                      pts={byRole.get(r)?.points ?? pointsFor(r)}
                      assignment={byRole.get(r)}
                      position={positionByRole.get(r)}
                      onAssign={(resourceId) => assignMutation.mutate({ resourceId, role: r })}
                      onUnassign={() => unassignMutation.mutate(r)}
                      onPointsChange={(points) => updatePointsMutation.mutate({ role: r, points })}
                      pointsSaving={updatePointsMutation.isPending}
                      atlasOpen={openAtlasRole === r}
                      onAtlasToggle={() => setOpenAtlasRole((prev) => (prev === r ? null : r))}
                      onAtlasClose={() => setOpenAtlasRole(null)}
                      onOpenResource={setOpenResourceId}
                    />
                  ))}
                </div>
                {(assignMutation.isPending ||
                  unassignMutation.isPending ||
                  updatePointsMutation.isPending) && (
                  <div className="mt-2 text-[11px] text-muted-foreground">Saving…</div>
                )}
                {assignMutation.isError && (
                  <div className="mt-2 text-[11px] text-[var(--fit-at-risk)]">
                    Assignment failed.
                  </div>
                )}
                {updatePointsMutation.isError && (
                  <div className="mt-2 text-[11px] text-[var(--fit-at-risk)]">
                    {String((updatePointsMutation.error as Error).message).slice(0, 120)}
                  </div>
                )}
              </section>

              {browseOpen && (
                <section>
                  <ResourceBrowser
                    expectedDisciplineCodes={Array.from(
                      new Set((positions ?? []).map((p) => p.expectedDisciplineCode)),
                    )}
                  />
                </section>
              )}

              <SupportingSection
                supporting={supporting}
                onAdd={(resourceId) => assignSupportingMutation.mutate(resourceId)}
                onRemove={(assignmentId) => unassignSupportingMutation.mutate(assignmentId)}
                onPointsChange={(assignmentId, points) =>
                  supportingPointsMutation.mutate({ assignmentId, points })
                }
                adding={assignSupportingMutation.isPending}
                pending={supportingPointsMutation.isPending}
                onOpenResource={setOpenResourceId}
              />

              {data.upcomingPodSchedule.length > 0 && (
                <section>
                  <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Scheduled Pod Changes
                  </div>
                  <div className="space-y-1.5">
                    {data.upcomingPodSchedule.map((s) => (
                      <div
                        key={s.id}
                        className="flex items-center gap-2 rounded-md border border-[var(--intensity-8)]/30 bg-[var(--intensity-8)]/5 px-3 py-1.5 text-[12px]"
                      >
                        <CalendarClock className="h-3.5 w-3.5 text-[var(--intensity-8)]" />
                        <span className="flex-1">
                          Switch to{" "}
                          <span className="font-semibold">
                            {s.shapeName ?? s.composition}
                            {s.shapeName ? ` (${s.composition})` : ""}
                          </span>{" "}
                          on {new Date(s.effectiveStart).toLocaleDateString()}
                        </span>
                        <button
                          type="button"
                          onClick={() => cancelSchedule.mutate(s.id)}
                          className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-[var(--fit-at-risk)]"
                          aria-label="Cancel scheduled change"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <section>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Constraints
                </div>
                <SolutionConstraintsEditor solutionId={id} constraints={data.constraints} />
              </section>
            </div>
          ) : tab === "fit" ? (
            <div className="space-y-4">
              {hasConstraints ? (
                <>
                  <FitRadar snapshot={data.fitSnapshot} hasR1={hasR1} />
                  <ExpectationBreakdown data={expectationBreakdownData} />
                </>
              ) : (
                <div className="rounded-md border border-border bg-card p-4 text-[12px] text-muted-foreground">
                  <div className="mb-1 font-semibold text-foreground">Fit Score unavailable</div>
                  No Constraints are defined for this Solution, so there is nothing to score
                  Constraint Match against. Add Constraints on the{" "}
                  <span className="font-medium text-foreground">Pod &amp; Fit</span> tab to enable
                  the Fit Score. You can still assign Resources to the Pod directly.
                </div>
              )}
              <div className="rounded-md border border-border bg-card p-3">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  Intensity (preview)
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={1}
                    max={INTENSITY_MAX}
                    value={previewIntensity ?? data.intensity}
                    onChange={(e) => setPreviewIntensity(Number(e.target.value))}
                    className="flex-1 accent-[var(--intensity-8)]"
                  />
                  <span className="w-10 text-right font-display text-[18px] font-bold">
                    {previewIntensity ?? data.intensity}
                  </span>
                </div>
                <div className="mt-2 flex items-center justify-between">
                  <IntensityIndicator level={previewIntensity ?? data.intensity} />
                  <IntensityHoursHint intensity={previewIntensity ?? data.intensity} />
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  Drag to preview how the current Pod's allocated points meet a different Intensity
                  target.
                </p>
                <div className="mt-3">
                  <IntensityAllocationBar
                    intensity={previewIntensity ?? data.intensity}
                    allocations={allocations}
                    supportingPoints={supportingPoints}
                  />
                </div>
              </div>
            </div>
          ) : tab === "demand" ? (
            <SolutionDemandLinks solutionId={id} />
          ) : tab === "hours" ? (
            <div className="space-y-8">
              <SolutionWorkloadChart solutionId={id} />
              <SolutionPipelineChart solutionId={id} />
            </div>
          ) : (
            <SolutionSignalsSection solutionId={id} />
          )}
        </div>
      </aside>
      {openResourceId && (
        <ResourceDetailDrawer id={openResourceId} onClose={() => setOpenResourceId(null)} />
      )}
    </>
  );
}
